import { FormControl, FormField, FormItem, FormLabel, FormMessage, SingleToggleGroup, ToggleGroupOption } from "@/shared/components";
import { FieldPath, FieldValues, UseFormReturn } from "react-hook-form";

type SingleToggleGroupFormFieldProps<T extends FieldValues, OptionType extends string> = {
    form: UseFormReturn<T>;
    label?: string;
    name: FieldPath<T>;
    options: ToggleGroupOption<OptionType>[];
    className?: string;
    labelClassName?: string;
    readOnly?: boolean;
    onValueChange?: (value: OptionType) => void;
    optional?: boolean;
    dataTestId?: string;
};

export function SingleToggleGroupFormField<T extends FieldValues, OptionType extends string>({
    form,
    name,
    options,
    label,
    readOnly,
    optional,
    labelClassName,
    onValueChange,
    dataTestId,
}: SingleToggleGroupFormFieldProps<T, OptionType>) {
    return (
        <FormField
            control={form.control}
            name={name}
            render={({ field }) => {
                const handleValueChange = (value: OptionType) => {
                    field.onChange(value);
                    onValueChange?.(value);
                };

                return (
                    <FormItem>
                        {label && (
                            <FormLabel optional={optional} className={labelClassName}>
                                {label}
                            </FormLabel>
                        )}
                        <FormControl>
                            {readOnly ? (
                                <span className="w-full">
                                    {options.find((option) => option.value === field.value)?.label || "Onbekend"}
                                </span>
                            ) : (
                                <SingleToggleGroup
                                    options={options}
                                    {...field}
                                    onValueChange={handleValueChange}
                                    data-testid={dataTestId}
                                />
                            )}
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                );
            }}
        />
    );
}
